import camelCase from 'lodash/camelCase';

import { capitalizeFirstLetter } from '@lumapps/utils/string/capitalizeFirstLetter';
import type { CamelCase } from '@lumapps/utils/types/CamelCase';

import { Route } from '../types';
import { createPageRoute, CreatePageRouteOptions } from './createPageRoute';

type RouteGetter = (id: string, additionalParams?: Route['params'], query?: Route['query']) => Route;
type DefaultRoutes = {
    // base route, used for listing entities
    baseRoute: Route;
    // route for creating an entity
    createRoute: Route;
    // route for editing an entity
    editRoute: Route;
    // route for viewing an entity
    viewRoute: Route;
};
type DefaultRouteGetters = {
    // dynamically creates a URL for listing entities
    getBaseRoute: (query?: Route['query']) => Route;
    // dynamically creates a URL for creating an entity
    getCreateRoute: (query?: Route['query']) => Route;
    // dynamically creates a URL for editing an entity
    getEditRoute: RouteGetter;
    // dynamically creates a URL for viewing an entity
    getViewRoute: RouteGetter;
};
type CustomRoutes<T extends string> = {
    [K in T as `${CamelCase<K>}Route`]: Route;
};
type CustomRouteGetters<T extends string> = {
    [K in T as `get${Capitalize<CamelCase<K>>}Route`]: RouteGetter;
};
type CreateEntityManagementRoutes<T extends string> = DefaultRoutes &
    DefaultRouteGetters &
    CustomRoutes<T> &
    CustomRouteGetters<T>;

/**
 * From a given route it generates a set of routes to be used for managing entities, including the base route,
 * create and edit routes.
 * @param options
 * @param additionalModes
 */
export const createEntityManagementRoutes = <T extends string>(
    options: CreatePageRouteOptions,
    additionalModes: T[] = [],
): CreateEntityManagementRoutes<T> => {
    // first, we generate all the base routes by hand (base, edit & create) and we add a /:id/${mode} route for each string received in otherModes
    const allRoutes = additionalModes.reduce(
        (acc, curr: string) => ({
            ...acc,
            [`${camelCase(curr)}Route`]: createPageRoute({ ...options, mode: curr }),
        }),
        {
            baseRoute: createPageRoute(options),
            createRoute: createPageRoute({ ...options, mode: 'create' }),
            editRoute: createPageRoute({ ...options, mode: 'edit' }),
            viewRoute: createPageRoute({ ...options, mode: 'view' }),
        },
    ) as DefaultRoutes & CustomRoutes<T>;

    // then we create generators of route getter
    const generateQueryRouteGetter =
        (name: keyof typeof allRoutes) =>
        (query?: Route['query']): Route => ({
            ...(allRoutes[name] as Route),
            ...(query ? { query } : {}),
        });
    const generateFullRouteGetter =
        (name: keyof typeof allRoutes): RouteGetter =>
        (id, additionalParams, query) => ({
            ...(allRoutes[name] as Route),
            params: {
                ...(additionalParams || {}),
                id,
            },
            ...(query ? { query } : {}),
        });

    // and finally, we generate the getter for edit and each route in otherModes
    const allGetters = additionalModes.reduce(
        (acc, curr: string) => ({
            ...acc,
            [`get${capitalizeFirstLetter(camelCase(curr))}Route`]: generateFullRouteGetter(
                `${camelCase(curr)}Route` as keyof CustomRoutes<T>,
            ),
        }),
        {
            getBaseRoute: generateQueryRouteGetter('baseRoute'),
            getCreateRoute: generateQueryRouteGetter('createRoute'),
            getEditRoute: generateFullRouteGetter('editRoute'),
            getViewRoute: generateFullRouteGetter('viewRoute'),
        },
    ) as DefaultRouteGetters & CustomRouteGetters<T>;

    return { ...allRoutes, ...allGetters };
};
